import {
  Microscope,
  Wind,
  Droplets,
  ThermometerSun,
  FlaskConical,
  ShieldCheck,
  ClipboardCheck,
  Building2,
  Fan,
  HardHat,
  FileSearch,
  Hammer,
} from "lucide-react";

export const SERVICES = [
  {
    slug: "mold-assessments",
    title: "Mold Assessments",
    icon: Microscope,
    summary:
      "Independent, science-based mold investigations to identify conditions, sources of moisture, and inform a remediation protocol.",
    why: "An accurate mold assessment removes guesswork before a single dollar is spent on remediation.",
    when: "After visible growth, musty odors, water intrusion, occupant health concerns, or before/after a remediation project.",
    who: "Homeowners, property managers, healthcare facilities, schools, attorneys, insurance carriers, and realtors.",
    how: "Visual inspection, moisture mapping, thermal imaging, air & surface sampling, lab analysis, and a written report.",
    equipment: ["Hygrometer", "Pin & pinless moisture meters", "Thermal cameras", "Air pumps & cassettes", "Surface tape lifts & swabs"],
    standards: ["IICRC S520 — Mold Remediation", "ANSI/IICRC S540", "ACGIH Bioaerosols"],
    lab: "AIHA-LAP accredited laboratories. Spore trap counts (ASTM D7391), direct exam, and culture as warranted.",
    interpretation:
      "Indoor results are interpreted against simultaneous outdoor controls, building science context, and reservoir indicators (Stachybotrys, Chaetomium, Memnoniella).",
  },
  {
    slug: "indoor-air-quality",
    title: "Indoor Air Quality (IAQ)",
    icon: Wind,
    summary:
      "Comprehensive IAQ investigations covering particulates, VOCs, CO/CO₂, humidity, temperature, and bioaerosols.",
    why: "Most people spend 90% of their time indoors. IAQ directly impacts occupant health, productivity, and asset value.",
    when: "Occupant complaints, post-renovation, new construction commissioning, HVAC concerns, ongoing facility monitoring.",
    who: "Schools, hospitals, offices, multi-family, industrial facilities, and concerned homeowners.",
    how: "Multi-parameter logging, walkthroughs, ventilation assessment, bioaerosol sampling, and source identification.",
    equipment: ["IAQ multi-meters", "Particulate counters", "VOC monitors", "CO₂/CO sensors", "Data loggers"],
    standards: ["ASHRAE 62.1 / 62.2", "EPA IAQ Building Air Quality", "ACGIH Bioaerosols"],
    lab: "Targeted lab analyses (VOC TO-15, bioaerosol cultures) selected to answer the specific IAQ question.",
    interpretation:
      "Findings are contextualized with ventilation rates, occupancy, source proximity, and building science.",
  },
  {
    slug: "moisture-mapping",
    title: "Moisture Mapping",
    icon: Droplets,
    summary:
      "Forensic moisture investigations using meters, hygrometry, and thermal imaging to map the full extent of water intrusion.",
    why: "Hidden moisture is the single largest driver of mold growth and structural decay.",
    when: "After leaks, floods, slab seepage, roof intrusion, or any unexplained elevated humidity event.",
    who: "Restoration contractors, insurance carriers, attorneys, homeowners, and commercial property managers.",
    how: "Gridded moisture readings, equilibrium relative humidity, thermal differential mapping, and substrate-specific testing.",
    equipment: ["Pin/pinless moisture meters", "In-situ probes", "Thermal cameras", "Psychrometric tools"],
    standards: ["IICRC S500 — Water Damage Restoration", "IICRC S520"],
    lab: "Laboratory rarely required; field measurements drive the protocol.",
    interpretation:
      "Wet/dry boundaries are documented to scope drying, demolition, and post-remediation verification.",
  },
  {
    slug: "thermal-imaging",
    title: "Thermal Imaging",
    icon: ThermometerSun,
    summary:
      "Infrared thermography to non-destructively reveal moisture intrusion, missing insulation, HVAC issues, and envelope failures.",
    why: "Thermal imaging surfaces invisible problems before they become expensive ones.",
    when: "During mold/IAQ assessments, building envelope investigations, energy audits, and post-loss documentation.",
    who: "Commercial owners, schools, hospitals, and residential clients.",
    how: "Calibrated FLIR cameras under controlled differential conditions; correlated with moisture readings.",
    equipment: ["FLIR thermal cameras", "Reference temperature probes"],
    standards: ["ASTM C1060 — IR Inspection of Buildings", "ASNT Level I/II practices"],
    lab: "Not applicable.",
    interpretation:
      "Anomalies are confirmed with moisture meters and visual inspection — thermal alone never diagnoses.",
  },
  {
    slug: "water-damage-assessments",
    title: "Water Damage Assessments (Cat 1/2/3)",
    icon: FlaskConical,
    summary:
      "Independent categorization and scoping of water losses per IICRC S500, including sewage and Category 3 contamination.",
    why: "Mis-categorized losses lead to under-remediation, recontamination, and health risk.",
    when: "Immediately after a loss event, or as an independent third-party review of a contractor's scope.",
    who: "Insurance carriers, attorneys, property managers, homeowners.",
    how: "Category determination, class assessment, affected materials inventory, drying goal documentation.",
    equipment: ["Moisture meters", "Hygrometers", "Coliform/E. coli surface kits", "Thermal cameras"],
    standards: ["IICRC S500", "ANSI/IICRC S540 (Trauma & Sewage)"],
    lab: "Surface total coliform and E. coli testing where Category is in question.",
    interpretation:
      "Categorization is justified in writing with measurable evidence — not assumption.",
  },
  {
    slug: "remediation-protocols",
    title: "Remediation Protocol Writing",
    icon: ClipboardCheck,
    summary:
      "Detailed, defensible written protocols specifying scope, containment, engineering controls, PPE, and clearance criteria.",
    why: "A clear protocol holds remediation contractors accountable to a measurable standard of care.",
    when: "After an assessment identifies remediation is warranted.",
    who: "Property owners, insurance carriers, attorneys, and contractors who want an independent scope.",
    how: "Protocols are tailored to the structure, contamination level, occupants, and applicable standards.",
    equipment: ["N/A — analytical/consulting deliverable"],
    standards: ["IICRC S520", "EPA Mold Remediation in Schools & Commercial Buildings"],
    lab: "References pre-existing sampling; PRV criteria are specified.",
    interpretation:
      "Protocols define the *what* and *how much* — never the *who* (we remain independent of remediation).",
  },
  {
    slug: "post-remediation-verification",
    title: "Post-Remediation Verification (PRV)",
    icon: ShieldCheck,
    summary:
      "Independent clearance assessments to confirm remediation success before reconstruction begins.",
    why: "PRV is the only way to verify a job was actually done right — independently.",
    when: "After remediation is complete and before reconstruction.",
    who: "Property owners, insurance carriers, GCs, and remediation contractors seeking third-party clearance.",
    how: "Visual inspection of containment, moisture verification, post-remediation air & surface sampling.",
    equipment: ["Air sampling pumps", "Surface sampling media", "Moisture meters", "Hygrometers"],
    standards: ["IICRC S520 — Section on PRV", "NYC Guidelines (where adopted)"],
    lab: "AIHA-LAP labs; counts compared against outdoor controls and clearance criteria.",
    interpretation:
      "Clearance is granted only when criteria are met. Hurtler never modifies criteria to fit a result.",
  },
  {
    slug: "commercial-consulting",
    title: "Commercial Environmental Consulting",
    icon: Building2,
    summary:
      "Ongoing environmental consulting for facilities — IAQ programs, due diligence, expert witness, and litigation support.",
    why: "Complex portfolios benefit from a strategic, scientific environmental partner.",
    when: "Acquisitions, occupant complaints, regulatory review, litigation, ongoing IAQ programs.",
    who: "Commercial owners, REITs, schools, hospitals, attorneys, government agencies.",
    how: "Engagement scoping, site investigation, reporting, and ongoing advisory.",
    equipment: ["Full Hurtler field stack"],
    standards: ["ASHRAE", "IICRC", "EPA", "OSHA"],
    lab: "Engagement-specific.",
    interpretation:
      "Strategic, transparent, and built to withstand scrutiny.",
  },
  {
    slug: "hvac-mold-assessments",
    title: "HVAC Mold Assessments",
    icon: Fan,
    summary:
      "Targeted investigations of air handlers, coils, plenums, and duct systems for microbial amplification.",
    why: "HVAC is the most efficient distributor of contaminants in any building — assessing it is critical.",
    when: "Visible biofilm, musty supply air, post-flood, or as part of any whole-building IAQ assessment.",
    who: "Commercial facilities, schools, hospitals, hospitality, and high-end residential.",
    how: "Inspection of coils, drain pans, plenums; surface and air sampling at registers and AHU.",
    equipment: ["Inspection scopes", "Surface sampling media", "Particulate counters"],
    standards: ["NADCA ACR", "ASHRAE", "IICRC S520"],
    lab: "Surface direct exam and air spore counts.",
    interpretation:
      "Findings drive HVAC-specific protocols (cleaning, coil treatment, component replacement).",
  },
  // ----- Asbestos vertical (expansion) -----
  {
    slug: "asbestos-inspection",
    title: "Asbestos Inspection",
    icon: HardHat,
    summary:
      "Pre-renovation and pre-demolition asbestos inspections of buildings constructed before 1981 — performed by trained, independent inspectors.",
    why: "Federal NESHAP and Texas TDSHS rules require inspection of pre-1981 structures before any renovation or demolition activity that disturbs suspect materials.",
    when: "Before renovation, demolition, real-estate transactions on older buildings, or as part of an environmental due-diligence package.",
    who: "Property owners, GCs, real-estate buyers/sellers, schools, public buildings, attorneys, and insurance carriers.",
    how: "Walk-through survey, homogeneous-area sampling, PLM/TEM laboratory analysis, written inspection report.",
    equipment: ["Sampling tools", "HEPA-equipped sampling kits", "Photographic documentation", "Chain-of-custody forms"],
    standards: ["EPA NESHAP (40 CFR 61, Subpart M)", "Texas Asbestos Health Protection Rules (25 TAC 295)", "OSHA 29 CFR 1926.1101"],
    lab: "NVLAP-accredited laboratories. PLM (EPA 600/R-93/116) and TEM as warranted.",
    interpretation:
      "Materials are categorized as ACM, presumed ACM, or non-ACM; recommendations align with regulatory thresholds.",
  },
  {
    slug: "asbestos-sampling",
    title: "Asbestos Sampling & Lab Analysis",
    icon: FileSearch,
    summary:
      "Targeted bulk and air sampling for suspect asbestos-containing materials with accredited laboratory analysis.",
    why: "Confirmatory sampling drives every subsequent decision — abatement scope, regulatory notification, and clearance.",
    when: "When suspect materials are identified during an asbestos inspection, or for periodic re-assessment of known ACM.",
    who: "Building owners, property managers, GCs, and abatement contractors needing independent third-party analysis.",
    how: "Bulk sampling of homogeneous areas per AHERA protocols; air sampling per NIOSH 7400/7402 as applicable.",
    equipment: ["Bulk sampling tools", "Air-O-Cell cassettes", "PCM-compatible cassettes", "Calibrated pumps"],
    standards: ["EPA AHERA (40 CFR 763)", "NIOSH 7400 / 7402", "OSHA Asbestos Standard"],
    lab: "NVLAP-accredited PLM and TEM analysis.",
    interpretation:
      "Results are reported with detection limits, method references, and clear actionability against regulatory thresholds.",
  },
  {
    slug: "asbestos-project-design",
    title: "Asbestos Project Design & Clearance",
    icon: Hammer,
    summary:
      "Independent abatement project design, monitoring, and clearance air sampling — never performing abatement itself.",
    why: "An independent designer and clearance professional ensures the abatement contractor is held to a measurable, defensible standard.",
    when: "Before, during, and after any asbestos abatement project subject to NESHAP or Texas TDSHS requirements.",
    who: "Property owners, school districts, public agencies, attorneys, and insurance carriers.",
    how: "Written project design specifying engineering controls, work practices, waste handling, and clearance criteria; final clearance air sampling.",
    equipment: ["Air sampling equipment", "TEM/PCM clearance kits", "Site monitoring tools"],
    standards: ["EPA NESHAP", "Texas Asbestos Health Protection Rules", "ASTM E2356"],
    lab: "NVLAP-accredited TEM (AHERA protocol) for clearance.",
    interpretation:
      "Clearance is granted only when criteria are met. Hurtler does not perform abatement — preserving structural independence.",
  },
];

export const SERVICES_INDEX = SERVICES.reduce((acc, s) => ({ ...acc, [s.slug]: s }), {});
